import { useEffect, useRef, useState } from 'react';
import { ArrowInline } from '@/lib/Icon';
import { useSearchParams } from 'react-router-dom';

import { apiFetch, ApiError } from '@/lib/api/client';
import { supabase } from '@/lib/supabase';
import { CheckoutClaimLayout } from './CheckoutClaimLayout';
import { markPostCheckoutSetup } from './postCheckoutSetup';

/**
 * Welcome / claim screen — entry point after Stripe redirects the user
 * back to `portal.systemlabs.com/?welcome=1&session_id=cs_...`.
 *
 * Stripe's webhook is async, so the patients row + pharmacy_orders row
 * may not exist when this screen first mounts. We poll the backend's
 * /checkout/claim-session with a short backoff (Stripe webhooks usually
 * fire within a few seconds; we cap recovery at 60s). This never retries payment.
 *
 * On success the backend returns a Supabase magic-link URL — we navigate
 * directly to it. Supabase Auth establishes the session and redirects to
 * /app/orders.
 */

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

function sameEmail(a: string | null | undefined, b: string): boolean {
  return typeof a === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase();
}

export interface ClaimResponse {
  /** Absent while the backend is still provisioning (a 2xx "pending"
   *  answer): treated like `404 session_not_ready`, so the screen retries. */
  action_link?: string;
  /** rx-store claims only: how many orders the checkout creates. */
  items?: number;
}

/** Retry delay after the n-th not-ready answer (house default). */
function defaultRetryDelay(retries: number): number {
  return Math.min(POLL_INTERVAL_MS * retries, 8000);
}

type Status =
  | 'pending'
  | 'failed'
  | 'expired'
  // Account already existed for this email before the checkout. Backend
  // refuses to auto-login — we ask Supabase to email a magic link to the
  // real address instead, so only the actual account owner can sign in.
  | 'account_pre_existed'
  | 'email_sent'
  | 'email_send_failed';

export function CheckoutClaimScreen({
  signedInEmail,
  onAlreadySignedIn,
  onClaimed,
  retryDelayMs = defaultRetryDelay,
}: {
  /** Storefront build only (RxRootRedirect): the email of this tab's
   *  session. When the checkout's account pre-existed and is that same
   *  account, no sign-in email is sent and `onAlreadySignedIn` runs
   *  instead. The house build passes neither, so it always emails. */
  signedInEmail?: string | null;
  onAlreadySignedIn?: () => void;
  /** Storefront build only: the claim's answer, just before the sign-in
   *  link opens (its `items` count tells the welcome screen how many
   *  orders to wait for). */
  onClaimed?: (claim: ClaimResponse) => void;
  /** Storefront build only: a shorter retry schedule while the payment
   *  webhook provisions the order. The house build keeps its own. */
  retryDelayMs?: (retries: number) => number;
} = {}) {
  const [params] = useSearchParams();
  // Read when the claim answers, not when the effect started.
  const signedInRef = useRef({ signedInEmail, onAlreadySignedIn, onClaimed, retryDelayMs });
  useEffect(() => {
    signedInRef.current = { signedInEmail, onAlreadySignedIn, onClaimed, retryDelayMs };
  }, [signedInEmail, onAlreadySignedIn, onClaimed, retryDelayMs]);
  // Two upstream flows funnel into this screen:
  //   1. Legacy Stripe Checkout Sessions → `?session_id=cs_...`
  //   2. New Healthie-flow Payment Element → Stripe's confirmPayment
  //      appends `?payment_intent=pi_...&payment_intent_client_secret=...`
  // The backend's /checkout/claim-session accepts either prefix.
  const sessionId = params.get('session_id') || params.get('payment_intent');
  const redirectStatus = params.get('redirect_status');
  const [status, setStatus] = useState<Status>('pending');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [preExistingEmail, setPreExistingEmail] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  async function sendMagicLinkEmail(email: string) {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/app/orders`,
          // Don't auto-create new auth users from this path — we already
          // know the user exists (that's what triggered this branch).
          shouldCreateUser: false,
        },
      });
      if (error) throw error;
      setStatus('email_sent');
    } catch (err) {
      setStatus('email_send_failed');
      setErrorMessage(
        (err as Error).message ?? 'Could not send sign-in email.',
      );
    }
  }

  useEffect(() => {
    if (!sessionId) return;
    setStatus('pending');
    setErrorMessage(null);
    // For the Payment Element flow Stripe may return a non-success status
    // (e.g. `requires_payment_method` after a card decline). Don't try to
    // claim a session that didn't actually complete.
    if (redirectStatus && redirectStatus !== 'succeeded') {
      setStatus('failed');
      setErrorMessage(
        'Payment did not complete. Please try again with a different card.',
      );
      return;
    }
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retries = 0;
    const controller = new AbortController();
    // Also bound an in-flight request: a hung API must not leave the loader
    // running forever. Retry only sign-in for this same checkout identifier.
    const deadline = setTimeout(() => {
      cancelled = true;
      clearTimeout(retryTimer);
      controller.abort();
      setStatus('failed');
      setErrorMessage(
        'Your account setup is taking longer than expected. Retry sign-in below; you do not need to purchase again.',
      );
    }, POLL_TIMEOUT_MS);

    async function attempt(): Promise<void> {
      if (cancelled) return;
      try {
        const res = await apiFetch<ClaimResponse>('/checkout/claim-session', {
          method: 'POST',
          body: JSON.stringify({ session_id: sessionId }),
          signal: controller.signal,
        });
        if (cancelled) return;
        if (!res?.action_link) {
          // Provisioning still running (a "pending" answer): retry.
          retries += 1;
          retryTimer = setTimeout(attempt, signedInRef.current.retryDelayMs(retries));
          return;
        }
        clearTimeout(deadline);
        signedInRef.current.onClaimed?.(res);
        // Hard navigate — Supabase Auth handles the magic-link redirect
        // chain back to /app/orders. Keep the setup screen up across it.
        markPostCheckoutSetup();
        window.location.href = res.action_link;
      } catch (err) {
        if (cancelled) return;
        const retryable =
          err instanceof ApiError
            ? (err.status === 404 && err.code === 'session_not_ready') ||
              [500, 502, 503, 504].includes(err.status)
            : err instanceof TypeError; // fetch network failure, not arbitrary errors
        if (retryable) {
          // One request at a time, with capped backoff during an outage.
          retries += 1;
          retryTimer = setTimeout(attempt, signedInRef.current.retryDelayMs(retries));
          return;
        }
        clearTimeout(deadline);
        if (err instanceof ApiError) {
          if (err.status === 410 && err.code === 'session_expired') {
            setStatus('expired');
            setErrorMessage(
              'This checkout link has expired. Please sign in or contact support.',
            );
            return;
          }
          if (err.status === 409 && err.code === 'account_pre_existed') {
            // Backend refused to mint the auto-login link because this
            // email pre-existed (could be a returning customer OR a
            // third-party who paid using someone else's email). Either
            // way, only the inbox owner gets to sign in — ask Supabase
            // to email them a magic link from the client.
            const emailRaw =
              (err.details as { email?: string } | null)?.email ?? null;
            const signedIn = signedInRef.current;
            if (
              emailRaw &&
              signedIn.onAlreadySignedIn &&
              sameEmail(signedIn.signedInEmail, emailRaw)
            ) {
              // This tab is already signed in to the account that paid:
              // nothing to prove, so no email. Same hand-off as the link.
              markPostCheckoutSetup();
              signedIn.onAlreadySignedIn();
              return;
            }
            setPreExistingEmail(emailRaw);
            setStatus('account_pre_existed');
            if (emailRaw) {
              void sendMagicLinkEmail(emailRaw);
            }
            return;
          }
          setStatus('failed');
          setErrorMessage(err.message);
          return;
        }
        setStatus('failed');
        setErrorMessage((err as Error).message ?? 'Something went wrong.');
      }
    }

    attempt();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      clearTimeout(deadline);
      controller.abort();
    };
  }, [sessionId, redirectStatus, retryCount]);

  if (!sessionId) {
    return (
      <main className="min-h-screen flex items-center justify-center px-15">
        <div className="max-w-form w-full text-center">
          <p className="type-body-md text-ink-secondary">
            Missing checkout session. Please return to the marketing site and
            complete checkout, or sign in below.
          </p>
        </div>
      </main>
    );
  }

  return (
    <CheckoutClaimLayout
      pending={status === 'pending'}
      title={
        <>
          {status === 'pending' && 'Confirming your order…'}
          {status === 'failed' && 'Almost there'}
          {status === 'expired' && 'Link expired'}
          {status === 'account_pre_existed' && 'Check your email'}
          {status === 'email_sent' && 'Check your email'}
          {status === 'email_send_failed' && 'Almost there'}
        </>
      }
      message={
        <>
          {status === 'pending' &&
            "We're setting up your account. This usually takes a few seconds."}
          {(status === 'failed' || status === 'expired') &&
            (errorMessage ??
              "We couldn't complete sign-in automatically. Try signing in below.")}
          {status === 'account_pre_existed' && (
            <>
              An account already exists for{' '}
              <span className="text-ink">
                {preExistingEmail ?? 'this email'}
              </span>
              . We’re sending a sign-in link there now — open your inbox to
              continue.
            </>
          )}
          {status === 'email_sent' && (
            <>
              We sent a sign-in link to{' '}
              <span className="text-ink">
                {preExistingEmail ?? 'your email'}
              </span>
              . Click it to access your account — your new order is already on
              the way.
            </>
          )}
          {status === 'email_send_failed' &&
            (errorMessage ??
              "We couldn't send the sign-in email. Try signing in below.")}
        </>
      }
    >
      {status === 'failed' &&
        (!redirectStatus || redirectStatus === 'succeeded') && (
          <button
            type="button"
            onClick={() => setRetryCount((value) => value + 1)}
            className="block mt-25 type-label-md text-ink hover:underline"
          >
            Retry sign-in <ArrowInline />
          </button>
        )}
      {status !== 'pending' && (
        <a
          href="/login"
          className="inline-block mt-25 type-label-md text-ink hover:underline"
        >
          Sign in <ArrowInline />
        </a>
      )}
    </CheckoutClaimLayout>
  );
}
